/**
 * The units a machine's supplier row bills in and the arithmetic between them (docs/suppliers.md, "Einheit je
 * Preiszeile"). No imports: the supplier store and the navigation read it while the app starts, before Pinia (the
 * display helpers in `unit.js` reach the settings store through `number.js`). `unit.js` re-exports all of it.
 */

// The units a machine's supplier row may bill in (docs/suppliers.md, "Einheit je Preiszeile"). The machine keeps one
// of MACHINE_BILLING_UNITS for selling; a row without a unit of its own bills in the machine's.
export const MACHINE_PURCHASE_UNITS = ["HOUR", "DAY", "WEEK", "MONTH"]

// A supplier's rental days per week and month when its conditions name none (`conditions.rentalTerms` null).
export const DEFAULT_RENTAL_TERMS = { daysPerWeek: 5, daysPerMonth: 20 }

// What a day of a rental without usage costs, in percent of the day rate, when the supplier's conditions name nothing
// else (`conditions.rentalTerms.idleDays`): a weekday in full, Saturday, Sunday and a public holiday nothing.
export const DEFAULT_IDLE_DAYS = { weekdayPercent: 100, saturdayPercent: 0, sundayHolidayPercent: 0 }

/** The unit a machine's price row bills in: its own, else the machine's. */
export const rowUnitOf = (row, machineUnit) => row?.unit ?? machineUnit ?? null

/**
 * The rental days one unit of a row stands for: a day one, a week or month the supplier's rental days of it (the
 * row's `daysPerUnit`, else the defaults). Null for an hour, which is not counted in days.
 */
export function daysPerUnitOf(unit, daysPerUnit = null) {
  if (unit === "DAY") return 1
  if (unit === "WEEK") return Number(daysPerUnit) || DEFAULT_RENTAL_TERMS.daysPerWeek
  if (unit === "MONTH") return Number(daysPerUnit) || DEFAULT_RENTAL_TERMS.daysPerMonth
  return null
}

/** Rows compare within one family: Tag, Woche and Monat by the day, Stunde only with Stunde. */
export const unitFamily = unit => (unit === "HOUR" ? "HOUR" : unit ? "DAY" : null)

/**
 * A row's price in another unit of its family: Tag, Woche and Monat into one another by their rental days (a week of
 * 5 days at 1.250,00 is 250,00 a day). The same unit stays as it is; null where the two do not convert (Stunde
 * against Tag, Woche or Monat).
 */
export function convertUnitPrice(price, fromUnit, fromDays, toUnit, toDays = null) {
  if (price == null) return null
  if (fromUnit === toUnit) return Number(price)
  const from = daysPerUnitOf(fromUnit, fromDays)
  const to = daysPerUnitOf(toUnit, toDays)
  return from == null || to == null ? null : (Number(price) / from) * to
}

/**
 * The unit several rows' prices are compared in: `preferred` (the default row's), or a day where Tag, Woche and Monat
 * mix. Rows of the other family are not compared.
 */
export function comparisonUnit(units, preferred) {
  const family = unitFamily(preferred)
  const mixed = new Set(units.filter(unit => unitFamily(unit) === family)).size > 1
  return mixed && family === "DAY" ? "DAY" : preferred
}

/**
 * Whether a surcharge on a price per `rowUnit` makes a price per `sellingUnit`, as the backend allows it
 * (`MachineQuantity.converts`): the same unit, and a day from a week or a month. Stunde never against Tag.
 */
export const convertsInto = (rowUnit, sellingUnit) =>
  Boolean(rowUnit) &&
  (rowUnit === sellingUnit || (sellingUnit === "DAY" && (rowUnit === "WEEK" || rowUnit === "MONTH")))

/** The unit a project's or customer's machine is sold in: the one chosen (`sellingUnit`), else the machine's own. */
export const sellingUnitOf = machine => machine?.sellingUnit ?? machine?.unit ?? null

/**
 * A row's price per the unit the machine is sold in, as the backend works out a surcharge (`MachineQuantity.pricePer`):
 * the same unit as it is, a week or month per day. Null where a surcharge cannot be put on it (Stunde against Tag,
 * Woche or Monat): such a machine needs a fixed selling price.
 */
export function pricePerSellingUnit(price, rowUnit, daysPerUnit, sellingUnit) {
  if (price == null || !rowUnit || !sellingUnit) return price == null ? null : Number(price)
  if (rowUnit === sellingUnit) return Number(price)
  return convertsInto(rowUnit, sellingUnit) ? Number(price) / daysPerUnitOf(rowUnit, daysPerUnit) : null
}

/**
 * A machine's purchase prices as a surcharge works with them: `prices` (those of `row`, the default or the row chosen)
 * per the unit it is sold in (`sellingUnit`, the machine's own where none is chosen). In the same unit they stay as
 * they are; a currency that does not convert is left out, so the surcharge reads as one without a purchase price.
 */
export function pricesPerSellingUnit(prices, row, machineUnit, sellingUnit = machineUnit) {
  const unit = rowUnitOf(row, machineUnit)
  if (!prices || !unit || !sellingUnit || unit === sellingUnit) return prices
  return Object.fromEntries(
    Object.entries(prices)
      .map(([currency, price]) => [currency, pricePerSellingUnit(price, unit, row?.daysPerUnit, sellingUnit)])
      .filter(([, price]) => price != null)
  )
}
